import { FightAction, FightData, FighterOutcome } from '../models/fight';
import { isUsableLegacyId } from './legacyId';

export type WinnerCorner = 'red' | 'blue' | 'draw';

export interface ResolvedWinner {
  corner: WinnerCorner;
  /** UUID do vencedor; `null` no empate. É a chave confiável. */
  winnerNewId: string | null;
  /** Id legado do vencedor, ou `null` quando a luta não tem mapeamento. */
  winnerId: number | null;
  winnerName: string | null;
  loserNewId: string | null;
  loserId: number | null;
}

/** Normaliza o id legado de um lutador: `0`/`null`/`undefined` viram `null`. */
function toLegacyFighterId(value: unknown): number | null {
  return isUsableLegacyId(value) ? value : null;
}

/**
 * Resolve o vencedor da luta a partir do UUID escolhido no Select do resultado.
 *
 * A decisão é pelo UUID porque o id legado do lutador pode ser `0`, `null` ou
 * `undefined` em lutas sem mapeamento — e a comparação por legado fazia o
 * empate ser gravado como vitória do canto vermelho, ou "blue" virar "red"
 * quando os dois cantos tinham o mesmo id ausente.
 *
 * @param winnerNewId - UUID escolhido, ou `null` para empate
 * @param fight - a luta aberta
 * @returns o vencedor resolvido, ou `null` quando a seleção não corresponde a
 * nenhum dos cantos (o caller deve abortar)
 */
export function resolveWinner(
  winnerNewId: string | null | undefined,
  fight: Pick<
    FightData,
    | 'redCornerId'
    | 'blueCornerId'
    | 'legacyRedCornerId'
    | 'legacyBlueCornerId'
    | 'redCornerName'
    | 'blueCornerName'
  >,
): ResolvedWinner | null {
  const redId = toLegacyFighterId(fight.legacyRedCornerId);
  const blueId = toLegacyFighterId(fight.legacyBlueCornerId);

  // Empate: o Select usa `null`, e `''` cobre um estado limpo vindo do restore.
  if (winnerNewId === null || winnerNewId === '') {
    return {
      corner: 'draw',
      winnerNewId: null,
      winnerId: null,
      winnerName: null,
      loserNewId: null,
      loserId: null,
    };
  }

  if (!winnerNewId) return null;

  if (winnerNewId === fight.redCornerId) {
    return {
      corner: 'red',
      winnerNewId: fight.redCornerId,
      winnerId: redId,
      winnerName: fight.redCornerName ?? null,
      loserNewId: fight.blueCornerId ?? null,
      loserId: blueId,
    };
  }

  if (winnerNewId === fight.blueCornerId) {
    return {
      corner: 'blue',
      winnerNewId: fight.blueCornerId,
      winnerId: blueId,
      winnerName: fight.blueCornerName ?? null,
      loserNewId: fight.redCornerId ?? null,
      loserId: redId,
    };
  }

  return null;
}

/**
 * Descobre o UUID do vencedor de uma action `fightResult` já gravada.
 *
 * `extraFields.winnerNewId` é o campo novo; `authorNewId` já era gravado e
 * cobre as actions anteriores a ele. Em último caso o id legado é traduzido
 * para UUID pelo canto correspondente — sem isso, uma action antiga reabriria o
 * Select com um valor que não casa com nenhuma opção.
 *
 * @returns o UUID do vencedor, `null` no empate, ou `undefined` quando a action
 * não permite decidir
 */
export function winnerNewIdFromAction(
  action: {
    authorId?: number | null;
    authorNewId?: string;
    extraFields?: any | null;
  } | null | undefined,
  fight: Pick<
    FightData,
    'redCornerId' | 'blueCornerId' | 'legacyRedCornerId' | 'legacyBlueCornerId'
  >,
): string | null | undefined {
  if (!action) return undefined;

  const fromExtraFields = action.extraFields?.winnerNewId;
  if (fromExtraFields) return fromExtraFields;
  if (action.authorNewId) return action.authorNewId;

  const legacyWinnerId = action.extraFields?.winnerId ?? action.authorId;

  // Empate antigo: gravava `winnerId: null` sem nenhum UUID.
  if (legacyWinnerId == null) return null;

  if (
    isUsableLegacyId(legacyWinnerId) &&
    legacyWinnerId === fight.legacyRedCornerId
  ) {
    return fight.redCornerId ?? undefined;
  }

  if (
    isUsableLegacyId(legacyWinnerId) &&
    legacyWinnerId === fight.legacyBlueCornerId
  ) {
    return fight.blueCornerId ?? undefined;
  }

  return undefined;
}

/**
 * Escolhe o outcome de um canto dentro de `fightersOutcome`.
 *
 * A chave é o UUID. Casar pelo id legado quebrava em lutas sem mapeamento: com
 * os dois cantos em `null`, os dois `find` devolviam o MESMO slot e os painéis
 * vermelho e azul mostravam os mesmos números.
 *
 * @param outcomes - os dois outcomes, na ordem dos cantos
 * @param corner - ids do canto procurado
 * @param fallbackIndex - slot usado quando nenhum id identifica o canto
 * (0 = vermelho, 1 = azul, a ordem que o backend devolve)
 */
export function outcomeOfCorner(
  outcomes: FighterOutcome[] | undefined,
  corner: { newId?: string; legacyId?: number | null },
  fallbackIndex: 0 | 1,
): FighterOutcome | undefined {
  if (!outcomes?.length) return undefined;

  if (corner.newId) {
    const byUuid = outcomes.find(
      (outcome) => outcome.fighterNewId === corner.newId,
    );
    if (byUuid) return byUuid;
  }

  if (isUsableLegacyId(corner.legacyId)) {
    const byLegacy = outcomes.find(
      (outcome) => outcome.fighterId === corner.legacyId,
    );
    if (byLegacy) return byLegacy;
  }

  return outcomes[fallbackIndex];
}

/**
 * Decide se uma action é do canto vermelho — a cor da linha no log.
 *
 * Ordem: UUID do autor, id legado (quando utilizável) e, por último, o campo
 * `corner` que a própria action carrega. Comparar só pelo legado pintava TODAS
 * as linhas da mesma cor em lutas sem mapeamento: com `authorId` e
 * `legacyRedCornerId` ambos `null`, a igualdade valia para toda ação.
 */
export function isRedCornerAction(
  action: Pick<FightAction, 'authorId' | 'authorNewId' | 'corner'> | undefined,
  fight: Pick<
    FightData,
    'redCornerId' | 'blueCornerId' | 'legacyRedCornerId' | 'legacyBlueCornerId'
  >,
): boolean {
  if (action?.authorNewId) {
    if (action.authorNewId === fight.redCornerId) return true;
    if (action.authorNewId === fight.blueCornerId) return false;
  }

  if (isUsableLegacyId(action?.authorId)) {
    if (action?.authorId === fight.legacyRedCornerId) return true;
    if (action?.authorId === fight.legacyBlueCornerId) return false;
  }

  return action?.corner === 'red';
}
